import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  markNewsletterPromptDismissed,
  markNewsletterPromptShown,
  markNewsletterSubscribed,
  shouldSuppressNewsletterPrompt,
  subscribeToNewsletter,
} from "../../utils/newsletterSubscription";
import "./NewsletterPrompt.css";

const PROMPT_DELAY_MS = 30 * 1000;
const REQUIRED_SCROLL_PROGRESS = 0.35;
const EXIT_DURATION_MS = 260;

function NewsletterPrompt({ language = "EN" }) {
  const { pathname } = useLocation();
  const [timerReady, setTimerReady] = useState(false);
  const [scrollReady, setScrollReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const exitTimerRef = useRef(null);
  const isHome = pathname === "/";

  const copy =
    language === "CN"
      ? {
          title: "订阅更新",
          description: "Archive of Light 更新时，我们会通过邮件通知你。",
          label: "邮箱地址",
          placeholder: "你的邮箱",
          submit: "订阅",
          submitting: "提交中…",
          success: "订阅成功。之后有版本更新时，我们会邮件通知你。",
          invalid: "请输入有效的邮箱地址。",
          error: "暂时无法订阅，请稍后再试。",
          close: "关闭订阅提示",
        }
      : {
          title: "Subscribe Update",
          description:
            "We’ll let you know when Archive of Light releases an update.",
          label: "Email address",
          placeholder: "Your email",
          submit: "Subscribe",
          submitting: "Subscribing…",
          success:
            "You’re subscribed. We’ll email you when a new update is released.",
          invalid: "Please enter a valid email address.",
          error: "Unable to subscribe right now. Please try again.",
          close: "Close subscription prompt",
        };

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setTimerReady(false);
    setScrollReady(false);
    setIsVisible(false);
    setIsExiting(false);
    setStatus("idle");
    setEmail("");

    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (isHome || shouldSuppressNewsletterPrompt()) return undefined;

    const timerId = window.setTimeout(() => {
      setTimerReady(true);
    }, PROMPT_DELAY_MS);

    let animationFrame = null;
    const updateScrollProgress = () => {
      if (
        animationFrame &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(animationFrame);
      }
      animationFrame = window.requestAnimationFrame(() => {
        const root = document.documentElement;
        const scrollableDistance = Math.max(
          0,
          root.scrollHeight - window.innerHeight
        );
        const progress =
          scrollableDistance <= 16
            ? 1
            : window.scrollY / scrollableDistance;
        setScrollReady(progress >= REQUIRED_SCROLL_PROGRESS);
      });
    };

    updateScrollProgress();
    window.addEventListener("scroll", updateScrollProgress, { passive: true });
    window.addEventListener("resize", updateScrollProgress);

    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(updateScrollProgress)
        : null;
    resizeObserver?.observe(document.body);

    return () => {
      window.clearTimeout(timerId);
      if (
        animationFrame &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener("scroll", updateScrollProgress);
      window.removeEventListener("resize", updateScrollProgress);
      resizeObserver?.disconnect();
    };
  }, [isHome, pathname]);

  useEffect(() => {
    if (
      isHome ||
      !timerReady ||
      !scrollReady ||
      isVisible ||
      isExiting ||
      shouldSuppressNewsletterPrompt()
    ) {
      return;
    }

    markNewsletterPromptShown();
    setIsVisible(true);
  }, [isHome, timerReady, scrollReady, isVisible, isExiting]);

  useEffect(() => {
    if (!(isVisible || isExiting)) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") handleDismiss();
    };
    const handleSubscribedElsewhere = () => {
      setIsVisible(false);
      setIsExiting(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener(
      "archive:newsletter-subscribed",
      handleSubscribedElsewhere
    );
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener(
        "archive:newsletter-subscribed",
        handleSubscribedElsewhere
      );
    };
  });

  const closePrompt = () => {
    setIsExiting(true);
    exitTimerRef.current = window.setTimeout(() => {
      setIsVisible(false);
      setIsExiting(false);
      exitTimerRef.current = null;
    }, EXIT_DURATION_MS);
  };

  const handleDismiss = () => {
    if (isExiting) return;
    markNewsletterPromptDismissed();
    closePrompt();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || status === "loading") return;

    setStatus("loading");
    try {
      await subscribeToNewsletter({ email: trimmedEmail, language });
      setEmail("");
      setStatus("success");
      markNewsletterSubscribed({ notify: false });
    } catch (error) {
      setStatus(error.code === "invalid_email" ? "invalid" : "error");
    }
  };

  if (!(isVisible || isExiting) || isHome) return null;

  const statusMessage =
    status === "success"
      ? copy.success
      : status === "invalid"
      ? copy.invalid
      : status === "error"
      ? copy.error
      : "";

  return (
    <aside
      className={`newsletter-prompt ${
        isExiting ? "newsletter-prompt-exit" : "newsletter-prompt-enter"
      }`}
      role="dialog"
      aria-labelledby="newsletter-prompt-title"
      aria-describedby="newsletter-prompt-description"
    >
      <div className="newsletter-prompt-header">
        <h2 id="newsletter-prompt-title">{copy.title}</h2>
        <button
          type="button"
          className="newsletter-prompt-close"
          onClick={handleDismiss}
          aria-label={copy.close}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <p id="newsletter-prompt-description" className="newsletter-prompt-desc">
        {copy.description}
      </p>

      {status !== "success" && (
        <form className="newsletter-prompt-form" onSubmit={handleSubmit}>
          <div className="newsletter-prompt-fields">
            <input
              id="newsletter-prompt-email"
              aria-label={copy.label}
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              placeholder={copy.placeholder}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <button type="submit" disabled={status === "loading"}>
              {status === "loading" ? copy.submitting : copy.submit}
            </button>
          </div>
        </form>
      )}

      {statusMessage && (
        <p
          className={`newsletter-prompt-status newsletter-prompt-status-${status}`}
          aria-live="polite"
        >
          {statusMessage}
        </p>
      )}
    </aside>
  );
}

export default NewsletterPrompt;
